import { css } from '@emotion/react';
import * as React from 'react';
import { toast } from 'react-toastify';

import { TaskChannel } from '../../interfaces/IQuickReply';
import { IScheduledCall, ScheduledInteractionFlow } from '../../interfaces/IScheduledCall';
import { useActiveConsents } from '../../queries/disclosures/useDisclosure';
import { useSendEmail, useSendSMS } from '../../queries/omni_channel_interactions/useInteractions';
import { QuickRepliesCategory, useCreateQuickReply } from '../../queries/omni_channel_interactions/useQuickReplies';
import usePerson from '../../queries/people/usePerson';
import { useScheduledInteractionsContactFlows } from '../../queries/scheduled_calls/useScheduledInteractions';
import authInfo from '../../services/authInfo';
import colors from '../../theme/colors';
import { spacings } from '../../theme/variables';
import { differenceInMilliseconds, formatDate, subMinutes, toDate } from '../../utils/date';
import { INTERNAL_DATE_FORMAT } from '../../utils/formatter';
import { playNotificationSound } from '../AudioNotification';
import Button, { ButtonSize, ButtonVariant } from '../Button/Button';
import alert from '../core/Alert';
import FlexBox from '../core/FlexBox';
import { HourglassIcon } from '../core/icons';
import Paragraph from '../core/Paragraph';
import Text from '../core/Text';

const ReminderBody = ({ call }: { call: IScheduledCall }): React.JSX.Element => {
  const { mutateAsync: createQuickReply, isPending: isPendingQuickReply } = useCreateQuickReply();
  const personGid = call.person_gid;

  const { data: person } = usePerson(personGid);
  const { data: consents = [] } = useActiveConsents(personGid);
  const smsConsentGiven = consents.find(consent => consent.kind === 'sms');
  const { mutate: sendEmail, isPending: isEmailSending } = useSendEmail();
  const { mutate: sendSms, isPending: isSmsSending } = useSendSMS();
  const customerEmail = person?.email;
  const customerPhoneNumber = call.phone;

  const sendReminder = () => {
    createQuickReply({
      personGid,
      categories: [QuickRepliesCategory.PipelineLateNotification],
      personFirstName: person?.first_name
    }).then(data => {
      const replies = data.quick_replies;
      if (smsConsentGiven) {
        const reply = replies.find(reply => reply.task_channel === TaskChannel.Sms);
        sendSms({
          to: [{ address: customerPhoneNumber }],
          smsBody: reply?.prefilled_content || '',
          personGid,
          engagementGid: call.engagement_gid!,
          quickReplyId: reply?.quick_reply_id
        });
      } else {
        const reply = replies.find(reply => reply.task_channel === TaskChannel.Email);
        sendEmail({
          to: [{ address: customerEmail || '' }],
          emailSubject: reply?.subject || '',
          emailBody: reply?.prefilled_content || '',
          personGid,
          engagementGid: call.engagement_gid!,
          quickReplyId: reply?.quick_reply_id
        });
      }
    });
  };

  return (
    <FlexBox columnDirection>
      <Paragraph mv={spacings.px4}>
        Reminder: Call with <Text className="fs-mask">{call.person_name}</Text> planned in 10 minutes.
      </Paragraph>
      <FlexBox
        gap={spacings.px8}
        mt={spacings.px20}
        css={css`
          flex-direction: row-reverse;
        `}
      >
        <Button size={ButtonSize.Small} onClick={() => toast.dismiss(call.id)}>
          Got it
        </Button>
        <Button
          loading={isEmailSending || isSmsSending || isPendingQuickReply}
          variant={ButtonVariant.Outline}
          size={ButtonSize.Small}
          onClick={() => {
            sendReminder();
            return toast.dismiss(call.id);
          }}
        >
          Notify customer I&apos;m late
        </Button>
      </FlexBox>
    </FlexBox>
  );
};

const FollowUpReminders = () => {
  const scheduledReminders = React.useRef<number[]>([]);
  const today = formatDate(new Date(), INTERNAL_DATE_FORMAT);

  const { data: scheduledInteractions } = useScheduledInteractionsContactFlows({
    assignees_emails: [authInfo.currentUserEmail].filter(Boolean),
    page: 1,
    max_run_at: today,
    min_run_at: today,
    flows: [ScheduledInteractionFlow.PipelineHighPriority],
    statuses: ['in_progress']
  });

  React.useEffect(() => {
    scheduledReminders.current.forEach(timerId => window.clearTimeout(timerId));
    scheduledReminders.current = [];

    if (!scheduledInteractions?.scheduled_interactions) {
      return;
    }
    scheduledInteractions.scheduled_interactions.forEach(followup => {
      const runAt = toDate(followup.run_at);
      if (!runAt) {
        return;
      }
      const delayMs = differenceInMilliseconds(subMinutes(runAt, 10), new Date());

      if (delayMs > 0) {
        const timerId = window.setTimeout(() => {
          playNotificationSound();
          alert({
            message: <ReminderBody call={followup} />,
            options: {
              icon: <HourglassIcon color={colors.blue400} />,
              toastId: followup.id,
              autoClose: 5 * 60_000
            }
          }).info();
        }, delayMs);

        scheduledReminders.current.push(timerId);
      }
    });

    return () => {
      scheduledReminders.current.forEach(timerId => window.clearTimeout(timerId));
      scheduledReminders.current = [];
    };
  }, [scheduledInteractions]);

  return null;
};

export default FollowUpReminders;
